import { useEffect, useState } from "react";
import { formatNumberID } from "../../../../../utils/FormatNumber";
import { format } from "date-fns";
import { Skeleton } from "primereact/skeleton";
import { AnimatePresence, motion } from "framer-motion";

const OwnerDashboardCustomerRank = (props) => {
  const { data, filterValue, loading } = props;
  const [customers, setCustomers] = useState([]);

  const RankItem = ({ item, index, type }) => {
    const handleClick = () => {
      window.location.href = `/report/customer-detail/${item.id}`;
    };
    return (
      <div
        className={`flex items-center justify-between gap-2 w-full px-2 py-1 rounded-lg shadow-sm hover:shadow-md hover:cursor-pointer hover:translate-y-[-2px] ease-in-out duration-300 ${
          item.rank <= 3
            ? "bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200"
            : "bg-gray-50"
        }`}
        onClick={handleClick}
      >
        {/* Left Section: Rank + Name */}
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`min-w-[2rem] min-h-[2rem] w-[2rem] h-[2rem] rounded-full flex items-center justify-center text-white text-sm font-bold ${
              item.rank === 1
                ? "bg-gradient-to-r from-yellow-400 to-yellow-600"
                : item.rank === 2
                ? "bg-gradient-to-r from-gray-400 to-gray-600"
                : item.rank === 3
                ? "bg-gradient-to-r from-yellow-600 to-yellow-800"
                : "bg-gradient-to-r from-purple-500 to-blue-600"
            }`}
          >
            {item.rank <= 3 ? (
              <i className="pi pi-crown text-lg"></i>
            ) : (
              item.rank
            )}
          </span>

          {/* Name with ellipsis and tooltip */}
          <div className="col-between-start gap-0.5 max-w-[100px] sm:max-w-[500px]">
            <span
              className="text-sm sm:text-md font-semibold whitespace-wrap overflow-ellipsis truncate max-w-[95px] sm:max-w-[500px]"
              title={item.name}
            >
              {item.name}
            </span>
            <span className="text-xs sm:text-sm whitespace-wrap overflow-ellipsis truncate max-w-[95px] xs:max-w-[500px] text-gray-700">
              {item.id}
            </span>
            <span
              className={`text-xs text-white font-medium bg-gradient-to-br rounded-md px-4 py-0.5 ${
                item.loyalty_level === "Gold"
                  ? "from-yellow-400 to-orange-500"
                  : item.loyalty_level === "Silver"
                  ? "from-gray-400 to-gray-950"
                  : "from-green-500 to-emerald-950"
              }`}
            >
              {item.loyalty_level}
            </span>
          </div>
        </div>

        {/* Right Section: Quantity or Revenue */}
        <div className="col-start-end">
          <span className="text-sm sm:text-lg font-bold bg-gradient-to-br from-green-400 to-emerald-500 text-transparent bg-clip-text whitespace-nowrap">
            {`Rp ${formatNumberID(item.revenue)}`}
          </span>
          <span className="text-xs text-black font-medium whitespace-nowrap">
            {item.receipts}
            {" transactions"}
          </span>
        </div>
      </div>
    );
  };

  const RankItemSkeleton = () => {
    return (
      <div className="flex items-center justify-between gap-2 w-full px-4 py-2 rounded-lg shadow-sm bg-gray-50">
        {/* Left: Circle + Name */}
        <div className="flex items-center gap-2 min-w-0">
          <Skeleton shape="circle" width="2rem" height="2rem" />
          <div className="col-center-start gap-2">
            <Skeleton width="5rem" height="1rem" />
            <Skeleton width="4rem" height="1rem" />
            <Skeleton width="5rem" height="1rem" />
          </div>
        </div>

        {/* Right: Number */}
        <div className="col-start-end gap-2">
          <Skeleton width="3rem" height="1rem" />
          <Skeleton width="5rem" height="1rem" />
        </div>
      </div>
    );
  };

  const generateCustomerData = () => {
    const newCustomers = data?.top_spender?.map((item, index) => ({
      name: item.customer_name,
      rank: index + 1,
      revenue: item.revenue,
      loyalty_level: item.level_name,
      receipts: item.receipts,
      id: item.customer_code ?? "JL_ID",
    }));
    setCustomers(newCustomers);
  };

  useEffect(() => {
    if (data) generateCustomerData();
  }, [data]);

  return (
    <div className="elevated-card-tight-vertical w-full !shadow-md col-center-start gap-4 border border-gray-300  !px-[1rem] sm:!px-6">
      <div className="flex flex-col items-start justify-between gap-0.5 w-full">
        <label className="row-start-center gap-2 text-xl font-semibold text-black">
          <i className="pi pi-trophy text-yellow-500 text-2xl"></i>
          Top 10 Customer Spenders
        </label>
        <div className="w-full row-between-center gap-2">
          <span className="text-xs bg-gradient-to-br from-green-200 to-emerald-200 text-black font-semibold border border-green-400 rounded-md px-2 py-0.5">
            {format(new Date(filterValue?.dari_tanggal), "dd MMM yyyy")} -{" "}
            {format(new Date(filterValue?.sampai_tanggal), "dd MMM yyyy")}
          </span>
          <button
            onClick={() => {
              window.open(
                `/report/customer-spending-report?start-date=${filterValue?.dari_tanggal}&end-date=${filterValue?.sampai_tanggal}`,
                "_blank"
              );
            }}
            className="text-sm text-slate-600 font-semibold rounded-md hover:text-slate-700 mr-1 underline"
          >
            [View all]
          </button>
        </div>
      </div>

      <div className="col-center-start w-full gap-2">
        <AnimatePresence mode="wait">
          <motion.div
            key={loading ? "loading" : "loaded"}
            initial={{ opacity: 0, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="col-center-start w-full gap-2"
          >
            {loading ? (
              [...Array(10)].map((_, idx) => <RankItemSkeleton key={idx} />)
            ) : customers.length < 1 ? (
              <span className="text-lg text-black">No data available</span>
            ) : (
              customers.map((item, index) => (
                <RankItem
                  key={`${item.name}-${item.rank}`}
                  item={item}
                  index={index}
                  type="customer"
                />
              ))
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OwnerDashboardCustomerRank;
